import { catalogItemKey, localizedItemDescription, localizedItemTitle } from 'features/result-content/resultItemCatalog';
import { typeLabelKey } from 'features/result-content/resultContent';
import { TagChipRow } from 'features/result-content/TagChip';
import { useFocusTrap } from 'layout/useFocusTrap';
import { Badge } from 'shared/components/Feedback';
import { Button } from 'shared/components/Button';
import { useEffect, useId, useMemo, useRef, useState } from 'react';

export function isCatalogItemAttached(item, attachedSet) {
  if (!item || !attachedSet) {
    return false;
  }
  return attachedSet.has(catalogItemKey(item))
    || attachedSet.has(String(item.id))
    || (item.result_item_id != null && attachedSet.has(String(item.result_item_id)));
}

export function ResultItemMultiSelect({
  catalogItems,
  visibleItems,
  selectedKeys,
  attachedKeys,
  onChange,
  onSelectAllVisible,
  onClear,
  onRemove,
  locale,
  t,
  disabled = false,
  canManage = true,
  id = 'attach-result-items',
  mixedTypesHint = true,
}) {
  const listId = `${id}-listbox`;
  const labelId = `${id}-label`;
  const reactId = useId();
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const attachedSet = useMemo(() => new Set(attachedKeys), [attachedKeys]);
  const selectedItems = useMemo(
    () => selectedKeys
      .map((key) => catalogItems.find((item) => catalogItemKey(item) === key))
      .filter(Boolean),
    [catalogItems, selectedKeys],
  );
  const selectableVisible = useMemo(
    () => visibleItems.filter((item) => !isCatalogItemAttached(item, attachedSet)),
    [attachedSet, visibleItems],
  );

  useFocusTrap(open, rootRef, {
    restore: false,
    onEscape: () => {
      setOpen(false);
      triggerRef.current?.focus();
    },
  });

  useEffect(() => {
    setActiveIndex(0);
  }, [visibleItems]);

  const previousSelectedCount = useRef(selectedKeys.length);
  useEffect(() => {
    if (previousSelectedCount.current > 0 && selectedKeys.length === 0) {
      setOpen(false);
    }
    previousSelectedCount.current = selectedKeys.length;
  }, [selectedKeys.length]);

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const toggle = (item) => {
    if (!canManage || disabled || isCatalogItemAttached(item, attachedSet)) {
      return;
    }
    const key = catalogItemKey(item);
    if (!key) {
      return;
    }
    if (selectedSet.has(key)) {
      onChange(selectedKeys.filter((value) => value !== key));
      return;
    }
    onChange([...selectedKeys, key]);
  };

  const optionId = (index) => `${id}-option-${reactId}-${index}`;

  const moveActive = (nextIndex) => {
    if (!visibleItems.length) {
      return;
    }
    const bounded = Math.max(0, Math.min(visibleItems.length - 1, nextIndex));
    setActiveIndex(bounded);
    const node = document.getElementById(optionId(bounded));
    node?.scrollIntoView({ block: 'nearest' });
  };

  const onTriggerKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setOpen(true);
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      close();
    }
  };

  const onListKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveActive(activeIndex + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveActive(activeIndex - 1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      moveActive(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      moveActive(visibleItems.length - 1);
    } else if (event.key === 'Enter' || event.key === ' ') {
      const item = visibleItems[activeIndex];
      if (item) {
        event.preventDefault();
        toggle(item);
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      close();
    }
  };

  const triggerLabel = selectedKeys.length
    ? t('connections.selectedCount', { count: selectedKeys.length })
    : t('connections.openPicker');

  return (
    <div className="result-item-multiselect" ref={rootRef} data-testid="result-item-multiselect">
      <div className="field">
        <span className="field-label" id={labelId}>{t('connections.attachItem')}</span>
        {selectedItems.length ? (
          <div className="chip-row" data-testid="selected-item-chips">
            {selectedItems.map((item) => {
              const title = localizedItemTitle(item, locale);
              const key = catalogItemKey(item);
              return (
                <span key={key} className="selection-chip">
                  <span>{title}</span>
                  {canManage && !disabled ? (
                    <button
                      type="button"
                      className="tag-chip-remove"
                      onClick={() => onRemove(key)}
                      aria-label={t('connections.removeSelection', { title })}
                    >
                      ×
                    </button>
                  ) : null}
                </span>
              );
            })}
          </div>
        ) : null}
        <div className="multiselect-toolbar wrap">
          <button
            ref={triggerRef}
            type="button"
            className="input multiselect-trigger"
            id={id}
            aria-label={triggerLabel}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={listId}
            disabled={disabled}
            onClick={() => setOpen((current) => !current)}
            onKeyDown={onTriggerKeyDown}
            data-testid="result-item-picker-trigger"
          >
            {triggerLabel}
          </button>
          {canManage && selectedKeys.length ? (
            <Button tone="ghost" disabled={disabled} onClick={onClear}>
              {t('connections.clearSelection')}
            </Button>
          ) : null}
        </div>
      </div>
      {open ? (
        <div className="multiselect-popover">
          {mixedTypesHint ? <p className="muted">{t('connections.mixedTypesHint')}</p> : null}
          {canManage ? (
            <div className="multiselect-actions wrap">
              <Button
                tone="secondary"
                disabled={disabled || selectableVisible.length === 0}
                onClick={onSelectAllVisible}
              >
                {t('connections.selectAllVisible')}
              </Button>
            </div>
          ) : null}
          <ul
            id={listId}
            role="listbox"
            aria-labelledby={labelId}
            aria-multiselectable="true"
            aria-activedescendant={visibleItems[activeIndex] ? optionId(activeIndex) : undefined}
            className="picker-list result-item-listbox"
            data-testid="result-item-listbox"
            tabIndex={0}
            onKeyDown={onListKeyDown}
          >
            {visibleItems.map((item, index) => {
              const key = catalogItemKey(item);
              const attached = isCatalogItemAttached(item, attachedSet);
              const selected = selectedSet.has(key);
              const title = localizedItemTitle(item, locale);
              const description = localizedItemDescription(item, locale);
              return (
                <li key={key}>
                  <div
                    id={optionId(index)}
                    role="option"
                    aria-label={title}
                    aria-selected={selected}
                    aria-disabled={attached || !canManage}
                    className={`picker-option result-item-option${index === activeIndex ? ' is-active' : ''}${attached ? ' is-attached' : ''}`}
                    data-testid="result-item-option"
                    data-item-key={key}
                    data-item-type={item.canonical_type || item.type}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => toggle(item)}
                  >
                    <input
                      type="checkbox"
                      tabIndex={-1}
                      checked={selected || attached}
                      disabled={attached || !canManage || disabled}
                      readOnly
                      aria-hidden="true"
                    />
                    <div className="result-item-option-body">
                      <div className="toolbar wrap">
                        <strong>{title}</strong>
                        <Badge>{t(typeLabelKey(item.canonical_type || item.type))}</Badge>
                      </div>
                      {description ? <p className="muted option-description">{description}</p> : null}
                      <TagChipRow tags={item.tags || []} locale={locale} t={t} />
                      {attached ? <p className="muted">{t('connections.alreadyAttached')}</p> : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
